import {FormArray, FormControl, Validators} from '@angular/forms';
import {CustomValidator, fromToDate} from '../../../../../../../common/forms/custom.validators';
import {
  ParcelAppEntityParcelWrite,
  RoadOccupationPurposeFeeAppEntityDictionaryRoadOccupationPurposeFee,
  RoadOccupationStageAppEntityApplicationRead
} from '../../../../../../../openapi';
import {calcDaysBetweenDates} from '../../../../../../../services/helpers';
import {
  RoadControlsForm,
  RoadControlsFormValue
} from '../../../../common/components/road-controls-set/road-controls.form';
import {environment} from '../../../../../../../../environments/environment';
import {
  RoadOccupationStagePurposeFeeItemForm,
  RoadOccupationStagePurposeFeeItemFormValue
} from '../../../../common/occupation-data-section/forms/road-occupation-stage-purpose-fee-item.form';

export function toAdPlacementStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): AdPlacementStageFormValue {
  return {
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,

    geometry: v?.geometry || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || [],

    dayQuantity: v?.dayQuantity || null,
    adKind: v?.adKind || '',
    adType: v?.adType || '',
    adMountingStructure: v?.adMountingStructure || '',
    adLightingType: v?.adLightingType || '',

    purposeFees: (v?.purposeFees || []).map((item) => ({
      width: item?.width || '',
      length: item?.length || '',
      area: item?.area || '',
      name: item?.purposeFee?.shortName || item?.purposeFee?.rateName || '',
      feeId: item?.purposeFee?.id + '' || '',
      rateId: item?.purposeFee?.rateId + '' || '',
      quantity: item?.quantity
    })),
  };
}

export interface AdPlacementStageFormValue extends RoadControlsFormValue {
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number | null;
  geometry: string | null;

  parcels: ParcelAppEntityParcelWrite[];

  adKind: string;
  adType: string;
  adMountingStructure: string;

  adLightingType: string;

  purposeFees: RoadOccupationStagePurposeFeeItemFormValue[];
}

export class AdPlacementStageForm extends RoadControlsForm {
  constructor() {
    super({
      startDate: new FormControl(null, {validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())]}),
      endDate: new FormControl(null, {validators: [Validators.required, CustomValidator.isValidDateFormat()]}),
      dayQuantity: new FormControl(null),
      geometry: new FormControl(null, environment.production ? Validators.required : []),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      adKind: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      adType: new FormControl('', {nonNullable: true, validators: [Validators.required]}),

      adMountingStructure: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adLightingType: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),

      purposeFees: new FormArray([])
    }, {
      validators: [
        fromToDate('startDate', 'endDate')
      ]
    });
  }

  public get purposeFees(): FormArray {
    return this.get('purposeFees') as FormArray;
  }

  public get purposeFeesForms(): RoadOccupationStagePurposeFeeItemForm[] {
    return this.purposeFees.controls as RoadOccupationStagePurposeFeeItemForm[];
  }

  public createPurposeFeesForms(
    fees: RoadOccupationPurposeFeeAppEntityDictionaryRoadOccupationPurposeFee[],
    formValue: RoadOccupationStagePurposeFeeItemFormValue[],
    quantityControl: boolean = false
  ): void {
    this.purposeFees.clear();
    fees.forEach(fee => {
      const f = new RoadOccupationStagePurposeFeeItemForm();

      if (quantityControl) {
        f.createQuantityControl();
      }

      // in current application this fields is required
      f.width.setValidators([Validators.required, Validators.maxLength(255)]);
      f.length.setValidators([Validators.required, Validators.maxLength(255)]);
      f.quantity?.setValidators([Validators.required, Validators.maxLength(255)]);
      f.area.setValidators([Validators.required, Validators.maxLength(255)]);
      f.setValidators([]);

      const value: RoadOccupationStagePurposeFeeItemFormValue | null = formValue
        .filter(v => v.feeId === (fee.id || '')).at(0) || null;

      f.name.setValue(fee?.shortName || fee?.rateName);
      f.feeId.setValue(fee.id + '');
      f.rateId.setValue(fee?.rateId + '');

      if (value) {
        f.width.setValue(Number(value.width).toFixed(2).replaceAll('.', ','));
        f.length.setValue(Number(value.length).toFixed(2).replaceAll('.', ','));
        f.area.setValue(Number(value.area).toFixed(2).replaceAll('.', ','));
        f.quantity?.setValue(value?.quantity || '');

        f.calcArea(this.adType.value === 'Dwustronna');
      }
      this.purposeFees.push(f);
    })
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get adType(): FormControl {
    return this.get('adType') as FormControl;
  }

  public adTypeChange(): void {
    const v = this.adType.value;
    this.purposeFeesForms.forEach(f => {
      f.calcArea(v === 'Dwustronna');
    });
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public override get valid(): boolean {
    return super.valid && !!this.purposeFeesForms.filter(f => !!f.values.area).length;
  }

  public override patchFormValues(value: AdPlacementStageFormValue): void {
    super.patchFormValues(value);
    this.purposeFeesForms.forEach(f => {
      const v = value.purposeFees.filter(v => v.feeId === f.feeId.value);
      f.patchFormValues(v.at(0) || {});
    });
  }

  public get values(): AdPlacementStageFormValue {
    return {
      ...this.value,
      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.street.value,

      purposeFees: this.purposeFeesForms.map(v => v.values)
    };
  }
}
